import dayjs from 'dayjs'
import {
  fetchBackendValue, fetchSiteGeo,
  fetchTrackCampaign, fetchTrackGeo, fetchTrackGeoById, fetchTrackKeywords, fetchTrackMedias, fetchTrackPlacements
} from '../../../../apis/segment'
import { fetchDataSourceByBrand, fetchSchema } from '../../../../apis/source'
import { DataType } from '../../../../constants/DataType'
import { Sources } from '../../../../constants/Sources'

// ***** This file is for each sources custom config ***** //

const { SITE, TRACK, LINE, LOCATION, FIRST_PARTY, GA4 } = Sources

// ----- Basic Configuration ----- //
const dataSourceParams = { includes: 'dataSourceType', maxResults: 9999 }
export const basicConf = {
  default: {
    logic: true, // AND, OR
    useAggregate: false, // can aggregate data
    extraSelector: false, // selector before category
    useCustomComponent: false, // custom all component in criteria setting
    useCategory: true,
    fetchAfterSelectCategory: false // directly fetch after category is selected
  },
  [LOCATION]: { logic: false, useCustomComponent: true }, // location will completely use different component
  [SITE]: {
    useAggregate: true,
    extraSelector: {
      label: 'site',
      name: 'siteId',
      fetch: (brand, type) => fetchDataSourceByBrand(brand, type, dataSourceParams)?.then(res => ({
        ...res, data: res?.data?.map(({ id, detailName2, name }) => ({ value: `${id}-${detailName2}-${name}`, label: name }))
      }))
    }
  },
  [TRACK]: {
    useAggregate: true,
    extraSelector: {
      label: 'campaign',
      name: 'campId',
      useTree: true,
      fetch: (brand, type) => fetchDataSourceByBrand(brand, type, dataSourceParams).then(async response => { // for track need to fetch brand, and then fetch each campaign under that brand
        if (response?.status !== 200) return response
        let all = []
        const data = response?.data || []
        const constructTree = ({ id, name }, selectable, parent) => ({ value: `${parent ? `${parent}-` : ''}${id}-${name}`, label: name, selectable })
        for (const i in data) {
          const { id, name, detailName2 } = data[i] || {}
          const res = await fetchTrackCampaign(detailName2)
          if (res?.status === 200) {
            all = [...all, { ...constructTree({ id, name }, false), children: res?.data?.map(i => constructTree(i, true, id)) } || []]
          }
        }
        return ({ status: 200, data: all?.map(i => ({ ...i, siteId: i?.id })) })
      })
    }
  },
  [LINE]: {
    useCategory: false,
    extraSelector: {
      label: 'lineLabel',
      name: 'type',
      fetch: (brand, type) => fetchDataSourceByBrand(brand, type, dataSourceParams)?.then(res => ({
        ...res, data: res?.data?.map(({ id, detailName, name }) => ({ value: `${id}-${detailName}-${name}`, label: name }))
      }))
    }
  },
  [FIRST_PARTY]: {
    useCategory: false,
    extraSelector: {
      label: 'dataSource',
      name: 'type',
      fetch: (brand, type) => fetchDataSourceByBrand(brand, type, dataSourceParams)?.then(res => ({
        ...res, data: res?.data?.map(({ id, detailName, name }) => ({ value: `${id}-${detailName}-${name}`, label: name }))
      }))
    },
    fetchAfterSelectCategory: true,
    logicOptApi: value => {
      const id = value?.split('-')[0]
      return fetchSchema(id).then(res => {
        const { data, ...other } = res
        const logicOptions = data.First_Party?.map(({ id, columnName, type }) => ({ value: `${columnName}-${type}`, label: columnName, type, fetch: type === STR ? () => {} : undefined, useBackendApi: true }))
        return { ...other, data: [{ value: 'default', label: 'column', logicOptions }] }
      })
    }
  },
  [GA4]: {
    extraSelector: {
      label: 'ga4',
      name: 'pass',
      fetch: (brand, type) => fetchDataSourceByBrand(brand, type, dataSourceParams)?.then(res => ({
        ...res, data: res?.data?.map(({ id, detailName2, name }) => ({ value: `${id}-${detailName2}-${name}`, label: name }))
      }))
    },
    fetchAfterSelectType: true,
    logicOptApi: (value, tableName) => {
      const id = value?.split('-')[0]
      return fetchSchema(id, { tableName }).then(res => {
        const { data, ...other } = res
        const logicOptions = Object.values(data)?.flat()?.map(({ id, columnName, type }) => ({ value: columnName, label: columnName, useBackendApi: true, type }))
        return { ...other, data: [{ value: 'default', label: 'column', logicOptions }] }
      })
    }
  }
}

// ----- Category Configuration ----- //
/* LOGIC OPTIONS DETAILS
  value: if falue have "-true" or "-false" means isTotal: true/false (this will be used as a params to give to backend)
  label
  type: data type that will determine which component and operator logic to use (SEE FIXED CONF -> DataType X Operator logic)
  fetch: api to fetch options in select. if fetch is function -> component: selector, if fetch is array -> component: tree.
  fetchDetail: because some have chaining fetch and backend only save last value of it, so here is the API for fetching the name and show it instead of id
  copyIfEmpty: for chaining fetch, if fetch is empty -> copy data from parent as children
  customDependency: custom selector dependency -> if true then every time custom selector value changed, the logic value fields will be reset
  useBackendApi: when using backend api, need to call api again when searching, and only fetch 10 data per fetch
  minValueLength: value need to be the exact length, will be filled in with 0
*/
const { STR, STR_WITHOUT_LIKE, BOOL, INT } = DataType
const formatForSelector = (res = {}, valueKey = 'id', labelKey = 'name') => ({ ...res, data: res?.data.map(i => ({ ...i, value: `${i?.[valueKey]}`, label: i?.[labelKey] })) })
const fetchBackendVal = params => fetchBackendValue(params)?.then(res => ({ ...res, ...res?.data, data: res?.data?.data?.map(i => ({ value: decodeURIComponent(i), label: decodeURIComponent(i) })) }))
export const categoryConf = ln_t => ({
  default: {
    options: undefined // contains: value, label, logicOptions (detail above)
  },
  [SITE]: {
    options: [
      {
        value: 'VISIT',
        label: ln_t('criteria.options.visit'),
        aggregateOptions: [
          { value: 'vTimes', label: ln_t('criteria.sites.vTimes') },
          { value: 'pv', label: ln_t('criteria.sites.pv') },
          { value: 'st', label: ln_t('criteria.sites.st') },
          { value: 'cnum', label: ln_t('criteria.sites.cnum') }
        ],
        logicOptions: [
          { value: 'geoId-false', label: ln_t('criteria.sites.geoId'), type: STR_WITHOUT_LIKE, fetch: () => fetchSiteGeo().then(res => formatForSelector(res, 'id', 'city')) },
          { value: 'lanPid-false', label: ln_t('criteria.sites.lanPid'), type: STR_WITHOUT_LIKE },
          { value: 'exPid-false', label: ln_t('criteria.sites.exPid'), type: STR_WITHOUT_LIKE },
          { value: 'vTimes-false', label: ln_t('criteria.sites.vTimes'), type: INT },
          { value: 'vTimes-true', label: ln_t('criteria.sites.vTimesTotal'), type: INT },
          { value: 'isNew-false', label: ln_t('criteria.sites.isNew'), type: BOOL },
          { value: 'isBo-false', label: ln_t('criteria.sites.isBo'), type: BOOL },
          { value: 'pv-false', label: ln_t('criteria.sites.pv'), type: INT },
          { value: 'pv-true', label: ln_t('criteria.sites.pvTotal'), type: INT },
          { value: 'st-false', label: ln_t('criteria.sites.st'), type: INT },
          { value: 'st-true', label: ln_t('criteria.sites.stTotal'), type: INT },
          { value: 'cnum-false', label: ln_t('criteria.sites.cnum'), type: INT },
          { value: 'cnum-true', label: ln_t('criteria.sites.cnumTotal'), type: INT },
          { value: 'refHost-false', label: ln_t('criteria.sites.refHost'), type: STR },
          { value: 'refPath-false', label: ln_t('criteria.sites.refPath'), type: STR },
          { value: 'browser-false', label: ln_t('criteria.sites.browser'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'browserVer-false', label: ln_t('criteria.sites.browserVer'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'os-false', label: ln_t('criteria.sites.os'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'osVer-false', label: ln_t('criteria.sites.osVer'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'isM-false', label: ln_t('criteria.sites.isM'), type: BOOL },
          { value: 'wifi-false', label: ln_t('criteria.sites.wifi'), type: BOOL },
          { value: 'mDevBra-false', label: ln_t('criteria.sites.mDevBra'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'mDevMod-false', label: ln_t('criteria.sites.mDevMod'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'var1-false', label: ln_t('criteria.sites.var1'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'var2-false', label: ln_t('criteria.sites.var2'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'var3-false', label: ln_t('criteria.sites.var3'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'var4-false', label: ln_t('criteria.sites.var4'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'var5-false', label: ln_t('criteria.sites.var5'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true }
        ]
      },
      {
        value: 'PAGEVIEW',
        label: ln_t('criteria.options.pageView'),
        aggregateOptions: [
          { value: 'p_cnum', label: ln_t('criteria.sites.p_num') },
          { value: 'p_st', label: ln_t('criteria.sites.p_st') }
        ],
        logicOptions: [
          { value: 'p_cnum-false', label: ln_t('criteria.sites.p_num'), type: INT },
          { value: 'p_isBo-false', label: ln_t('criteria.sites.p_isBo'), type: BOOL },
          { value: 'p_isEx-false', label: ln_t('criteria.sites.p_isEx'), type: BOOL },
          { value: 'p_isEn-false', label: ln_t('criteria.sites.p_isEn'), type: BOOL },
          { value: 'p_st-false', label: ln_t('criteria.sites.p_st'), type: INT },
          { value: 'p_st-true', label: ln_t('criteria.sites.p_stTotal'), type: INT },
          { value: 'p_refPid-false', label: ln_t('criteria.sites.p_refPid'), type: STR_WITHOUT_LIKE, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'p_url-false', label: ln_t('criteria.sites.p_url'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'p_tl-false', label: ln_t('criteria.sites.p_tl'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'p_host-false', label: ln_t('criteria.sites.p_host'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'p_path-false', label: ln_t('criteria.sites.p_path'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true }
        ]
      },
      {
        value: 'EVENT',
        label: ln_t('criteria.options.event'),
        aggregateOptions: [{ value: 'e_val', label: ln_t('criteria.sites.e_val') }],
        logicOptions: [
          { value: 'e_pid-false', label: ln_t('criteria.sites.e_pid'), type: STR_WITHOUT_LIKE },
          { value: 'e_cat-false', label: ln_t('criteria.sites.e_cat'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'e_act-false', label: ln_t('criteria.sites.e_act'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'e_lab-false', label: ln_t('criteria.sites.e_lab'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'e_val-false', label: ln_t('criteria.sites.e_val'), type: INT },
          { value: 'e_val-true', label: ln_t('criteria.sites.e_valTotal'), type: INT }

        ]
      }
    ]
  },
  [TRACK]: {
    options: [
      {
        value: 'AD_IMP',
        label: ln_t('criteria.options.exposure'),
        aggregateOptions: [{ value: 'impression', label: ln_t('criteria.options.exposure') }],
        logicOptions: [
          { value: 'mediaId-false', label: ln_t('criteria.track.mediaId'), type: STR_WITHOUT_LIKE, fetch: () => fetchTrackMedias().then(res => formatForSelector(res, 'id', 'name_cn')) },
          { value: 'spotId-false', label: ln_t('criteria.track.placement'), type: STR_WITHOUT_LIKE, fetch: campaignId => fetchTrackPlacements(campaignId).then(res => formatForSelector(res)), customDependency: true },
          {
            value: 'keyword-false',
            label: ln_t('criteria.track.keyword'),
            type: STR_WITHOUT_LIKE,
            fetch: [campaignId => fetchTrackPlacements(campaignId).then(res => formatForSelector(res)), placementId => fetchTrackKeywords(placementId).then(res => formatForSelector(res))],
            customDependency: true
          },
          { value: 'country-false', label: ln_t('criteria.track.country'), type: STR_WITHOUT_LIKE, fetch: () => fetchTrackGeo(0).then(res => formatForSelector(res)), minValueLength: 10 },
          {
            value: 'province-false',
            label: ln_t('criteria.track.province'),
            type: STR_WITHOUT_LIKE,
            fetch: [() => fetchTrackGeo(0).then(res => formatForSelector(res)), country => fetchTrackGeo(country).then(res => formatForSelector(res))],
            fetchDetail: id => fetchTrackGeoById(id).then(res => formatForSelector(res)),
            copyIfEmpty: true,
            minValueLength: 10
          },
          {
            value: 'city-false',
            label: ln_t('criteria.track.city'),
            type: STR_WITHOUT_LIKE,
            fetch: [
              () => fetchTrackGeo(0).then(res => formatForSelector(res)),
              country => fetchTrackGeo(country).then(res => formatForSelector(res)),
              province => fetchTrackGeo(province).then(res => formatForSelector(res))],
            fetchDetail: id => fetchTrackGeoById(id).then(res => formatForSelector(res)),
            copyIfEmpty: true,
            minValueLength: 10
          },
          { value: 'browser-false', label: ln_t('criteria.track.browser'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'device-false', label: ln_t('criteria.track.device'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'deviceModel-false', label: ln_t('criteria.track.deviceModel'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'deviceManufacture-false', label: ln_t('criteria.track.deviceManufacture'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'impression-false', label: ln_t('criteria.track.impression'), type: INT },
          { value: 'impression-true', label: ln_t('criteria.track.impressionTotal'), type: INT }
        ]
      },
      {
        value: 'AD_CLICK',
        label: ln_t('criteria.options.click'),
        aggregateOptions: [{ value: 'click', label: ln_t('criteria.options.click') }],
        logicOptions: [
          { value: 'mediaId-false', label: ln_t('criteria.track.mediaId'), type: STR_WITHOUT_LIKE, fetch: () => fetchTrackMedias().then(res => formatForSelector(res, 'id', 'name_cn')) },
          { value: 'spotId-false', label: ln_t('criteria.track.placement'), type: STR_WITHOUT_LIKE, fetch: campaignId => fetchTrackPlacements(campaignId).then(res => formatForSelector(res)), customDependency: true },
          {
            value: 'keyword-false',
            label: ln_t('criteria.track.keyword'),
            type: STR_WITHOUT_LIKE,
            fetch: [campaignId => fetchTrackPlacements(campaignId).then(res => formatForSelector(res)), placementId => fetchTrackKeywords(placementId).then(res => formatForSelector(res))],
            customDependency: true
          },
          { value: 'country-false', label: ln_t('criteria.track.country'), type: STR_WITHOUT_LIKE, fetch: () => fetchTrackGeo(0).then(res => formatForSelector(res)), minValueLength: 10 },
          {
            value: 'province-false',
            label: ln_t('criteria.track.province'),
            type: STR_WITHOUT_LIKE,
            fetch: [() => fetchTrackGeo(0).then(res => formatForSelector(res)), country => fetchTrackGeo(country).then(res => formatForSelector(res))],
            fetchDetail: id => fetchTrackGeoById(id).then(res => formatForSelector(res)),
            copyIfEmpty: true,
            minValueLength: 10
          },
          {
            value: 'city-false',
            label: ln_t('criteria.track.city'),
            type: STR_WITHOUT_LIKE,
            fetch: [
              () => fetchTrackGeo(0).then(res => formatForSelector(res)),
              country => fetchTrackGeo(country).then(res => formatForSelector(res)),
              province => fetchTrackGeo(province).then(res => formatForSelector(res))],
            fetchDetail: id => fetchTrackGeoById(id).then(res => formatForSelector(res)),
            copyIfEmpty: true,
            minValueLength: 10
          },
          { value: 'browser-false', label: ln_t('criteria.track.browser'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'device-false', label: ln_t('criteria.track.device'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'deviceModel-false', label: ln_t('criteria.track.deviceModel'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'deviceManufacture-false', label: ln_t('criteria.track.deviceManufacture'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'click-false', label: ln_t('criteria.track.click'), type: INT },
          { value: 'click-true', label: ln_t('criteria.track.clickTotal'), type: INT }
        ]
      }
    ]
  },
  [LINE]: {
    options: [
      {
        value: 'tag',
        logicOptions: [{ value: 'tag', label: ln_t('criteria.line.tag'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true }]
      },
      {
        value: 'profile',
        logicOptions: [
          { value: 'displayName', label: ln_t('criteria.line.displayName'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'statusMessage', label: ln_t('criteria.line.statusMessage'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'language', label: ln_t('criteria.line.language'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true }
        ]
      },
      {
        value: 'message',
        logicOptions: [
          { value: 'msgType', label: ln_t('criteria.line.msgType'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true },
          { value: 'msgContent', label: ln_t('criteria.line.msgContent'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true }
        ]
      },
      {
        value: 'event',
        logicOptions: [{ value: 'eventType', label: ln_t('criteria.line.eventType'), type: STR, fetch: fetchBackendVal, useBackendApi: true, customDependency: true }]
      }
    ]
  },
  [GA4]: {
    options: [
      { value: 'events' },
      { value: 'users' },
      { value: 'pseudonymous_users' }
    ]
  }
})

// ----- For Create / Modify -> Formating Rules Configuration ----- //
const getId = (item = '', minLength) => {
  if (dayjs(item).isValid()) return item
  const id = `${item}`.split('-')?.[0]
  return minLength ? `${id}${Array(minLength - id.length + 1).join('0')}` : id
}
const getName = (item = '', haveParents) => {
  const name = `${item}`.split('-') || []
  return haveParents ? name[1]?.split('@')?.[0] : name.splice(1, name.length - 1)?.join('-')
}
const getParent = (item = '') => item?.split('@')?.reverse()?.slice(0, -1)?.reduce((all, item) => ({
  arr: [...all?.arr || [], `${item}${all.prev ? `@${all.prev}` : ''}`],
  prev: `${item}${all.prev ? `@${all.prev}` : ''}`
}), {})?.arr

export const customReqBody = {
  default: ({ logic, type, ...other }) => {
    const group = {}
    Object.keys(other)?.forEach(key => {
      const keys = key.split('-')
      group[keys[0]] = {
        ...group[keys[0]] || {},
        [keys[1]]: keys.length <= 2 ? other[key] : { ...group[keys[0]]?.[keys[1]] || {}, [keys[2]]: other[key] }
      }
    })
    const UISetting = []
    const criteria = Object.values(group).map(data => {
      let formatData = { filters: [] }
      let formatSetting = []
      const catConf = categoryConf(i => i)?.[type]?.options?.find(i => i.value === data?.category) || {}
      Object.keys(data).forEach(key => {
        if (!Number(key)) {
          if (key === 'extras') {
            const { extraSelector } = basicConf[type] || {}
            const split = data[key]?.split('-')
            const extraSelectorVal = extraSelector && extraSelector !== 'pass' ? { [extraSelector?.name]: split[1] } : {}
            formatData = { ...formatData, dataSourceId: Number(split[0]), ...extraSelectorVal, extras: data[key] }
          } else if (key === 'aggregate') {
            if (data[key]) formatData.indic = data[key]
          } else formatData[key] = data[key]
        } else {
          const { col, operator, val } = data[key]
          const { fetch, minValueLength: min } = catConf.logicOptions?.find(i => i.value === col) || {}
          const split = `${col}`?.split('-') || []
          const colField = split.length === 1 ? { col: split[0] } : { col: split[0], isTotal: split[1] === 'true' }
          const formatVal = Array.isArray(val) ? val.map(i => getId(i, min))?.join(',') : getId(val, min)
          const valName = Array.isArray(val) ? val.map(i => getName(i, Array.isArray(fetch)))?.join(', ') : getName(val, Array.isArray(fetch))
          const parent = Array.isArray(fetch) ? Array.isArray(val) ? val.map(i => getParent(i))?.flat() : getParent(val) : []
          formatData.filters = [...formatData.filters || [], { ...colField, operator, val: formatVal }]
          formatSetting = [...formatSetting, { col, operator, val, name: valName, parent: [...new Set(parent)] }]
        }
      })
      UISetting.push({ ...formatData, setting: formatSetting })
      const { extras, ...other } = formatData
      return other
    })
    // const error = criteria?.some(({ indic, filters }) => !indic && (!filters || !filters?.length))
    return { custom: { rule: { logic, criteria }, UISetting }, error: false }
  },
  [LOCATION]: ({ code, locations }) => ({
    custom: {
      rule: {
        code,
        points: locations?.map(({ id, radius, latitude, longitude }) => ({ id, distance: radius, lat: latitude, lon: longitude }))
      }
    }
  })
}
